import type { Application } from 'pixi.js';
import { ExplorationData } from '../labyrinth/ExplorationData';
import type { Labyrinth} from '../labyrinth/Labyrinth';
import { Tile, type DoorDirection } from '../labyrinth/Labyrinth';
import { UpgradeSystem } from '../upgrades/UpgradeSystem';
import { StatsSystem } from '../upgrades/StatsSystem';
import { SoundSystem } from '../audio/SoundSystem';
import { TraderSystem } from '../upgrades/TraderSystem';
import { BuildingSystem } from '../buildings/BuildingSystem';
import { AchievementSystem } from '../achievements/AchievementSystem';
import { recomputeEquipStats } from '../achievements/Hooks';
import { getBuildingById as getBuildingDefById } from '../buildings/BuildingData';
import { getWeaponById } from '../upgrades/WeaponData';
import { migrate, type CurrentSave, CURRENT_SAVE_VERSION } from './SaveMigrations';

export interface GameState {
  enter(): void;
  exit(): void;
  update(dt: number): void;
}

const SAVE_KEY = 'remotewill_save';

function todayIsoDate(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export class GameStateManager {
  app: Application;
  private currentState: GameState | null = null;

  /** Persistent data across deploy/recall cycles */
  labyrinth: Labyrinth | null = null;
  exploration: ExplorationData | null = null;
  upgrades = new UpgradeSystem();
  stats = new StatsSystem();
  sound = new SoundSystem();
  trader = new TraderSystem();
  buildings = new BuildingSystem();
  achievements = new AchievementSystem();
  money = 0;

  /** Selected door for next deployment */
  selectedDoor: DoorDirection = 'north';

  constructor(app: Application) {
    this.app = app;

    this.load();
    this.wireAchievementHooks();
    this.bootSession();
  }

  /** Wire system-change callbacks so progression/stat/building achievements
   *  update reactively. Called once at construction and again after
   *  `resetProgress` rebuilds the subsystems. */
  private wireAchievementHooks() {
    this.upgrades.onEquipChange = (equipped) => {
      recomputeEquipStats(this.achievements, equipped);
    };
    this.stats.onChange = () => this.recomputeAchievementContext();
    this.buildings.onChange = (placed) => {
      // Count newly-placed buildings against lifetime total.
      const s = this.achievements.stats;
      if (placed) s.buildingsPlacedTotal++;
      this.recomputeAchievementContext();
    };
  }

  /** Increment session counter + track today's date. Called once per
   *  GameStateManager construction (i.e. once per app launch). */
  private bootSession() {
    const s = this.achievements.stats;
    s.totalSessions++;
    s.uniquePlayDays.add(todayIsoDate());
    this.recomputeAchievementContext();
  }

  /** Push live values from StatsSystem / BuildingSystem / inventory into the
   *  achievement context so that context-dependent achievements (level, stat
   *  thresholds, building diversity, weapon discovery derivatives, completion
   *  percent) re-evaluate against current state. */
  recomputeAchievementContext() {
    const ach = this.achievements;
    const s = this.stats.stats;
    ach.setContext('_level', this.stats.level);
    ach.setContext('_totalXp', this.stats.totalXp);
    ach.setContext('_statSpeed', s.speed);
    ach.setContext('_statPerception', s.perception);
    ach.setContext('_statEnergy', s.energy);
    ach.setContext('_statShield', s.shield);
    ach.setContext('_statStructure', s.structure);
    const sum = s.speed + s.perception + s.energy + s.shield + s.structure;
    ach.setContext('_statSum', sum);
    ach.setContext('_statMax', Math.max(s.speed, s.perception, s.energy, s.shield, s.structure));
    ach.setContext('_statMin', Math.min(s.speed, s.perception, s.energy, s.shield, s.structure));

    // Building diversity + maxed counts.
    const placed = this.buildings.buildings;
    const uniqueIds = new Set(placed.map((b) => b.id));
    ach.setContext('_buildingDiversity', uniqueIds.size);
    let maxed = 0;
    for (const b of placed) {
      const def = getBuildingDefById(b.id);
      if (def && b.level >= def.maxLevel) maxed++;
    }
    ach.setContext('_buildingsMaxed', maxed);

    // Weapon discovery derivatives.
    const discovered = ach.stats.weaponsUniqueDiscovered;
    const cats = new Set<string>();
    const tiers = new Set<number>();
    for (const id of discovered) {
      const def = getWeaponById(id);
      if (!def) continue;
      cats.add(def.category);
      tiers.add(def.tier);
    }
    ach.setContext('_categoriesDiscovered', cats.size);
    ach.setContext('_tiersDiscovered', tiers.size);

    // Snapshot level→runs min tracker (first time level hits 10).
    if (this.stats.level >= 10 && ach.stats.runsToLevel10 === 0) {
      ach.stats.runsToLevel10 = Math.max(1, ach.stats.totalDeployments);
    }

    // Completion percent (for Completionist achievement).
    const available = ach.totalAvailableTiers();
    ach.setContext(
      '_percentUnlocked',
      available > 0 ? Math.floor((ach.totalUnlockedTiers() / available) * 100) : 0,
    );
    ach.setContext('_challengeCompleted', ach.isCategoryComplete('Challenge') ? 1 : 0);

    ach.recheck();
  }

  // ─── Save / Load ───────────────────────────────────────

  /** Build the serialized envelope for both save() and exportSave(). */
  private buildSaveData(): Record<string, unknown> {
    const achSave = this.achievements.serialize();
    const data: Record<string, unknown> = {
      version: CURRENT_SAVE_VERSION,
      money: this.money,
      upgrades: this.upgrades.serialize(),
      stats: this.stats.serialize(),
      trader: this.trader.serialize(),
      buildings: this.buildings.serialize(),
      persistentStats: achSave.stats,
      achievements: achSave.achievements,
    };
    if (this.exploration) data.exploration = this.exploration.serialize();
    return data;
  }

  save() {
    try {
      localStorage.setItem(SAVE_KEY, JSON.stringify(this.buildSaveData()));
    } catch { /* storage full or unavailable — silently ignore */ }
  }

  private load(): boolean {
    let raw: string | null;
    try {
      raw = localStorage.getItem(SAVE_KEY);
    } catch {
      return false;
    }
    if (!raw) return false;

    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      // Corrupted JSON — treat as no save rather than propagating the throw.
      console.warn('[GameStateManager] save data is not valid JSON; starting fresh.');
      return false;
    }

    const data = migrate(parsed);
    if (!data) {
      console.warn('[GameStateManager] save version unrecognized or shape invalid; starting fresh.');
      return false;
    }

    this.applySave(data);
    return true;
  }

  /** Apply a validated save envelope to this manager. Subsystems are loaded
   *  independently so a single corrupt blob (e.g. stale trader format) does
   *  not nuke the rest of the progress. */
  private applySave(data: CurrentSave) {
    this.money = typeof data.money === 'number' && Number.isFinite(data.money) ? data.money : 0;
    this.tryLoad('upgrades',    () => data.upgrades    && this.upgrades.deserialize(data.upgrades as never));
    this.tryLoad('stats',       () => data.stats       && this.stats.deserialize(data.stats as never));
    this.tryLoad('trader',      () => data.trader      && this.trader.deserialize(data.trader as never));
    this.tryLoad('buildings',   () => data.buildings   && this.buildings.deserialize(data.buildings as never));
    this.tryLoad('exploration', () => {
      if (!data.exploration) return;
      this.exploration = ExplorationData.deserialize(data.exploration as never);
    });
    this.tryLoad('persistentStats', () => this.achievements.deserializeStats(data.persistentStats));
    this.tryLoad('achievements',    () => this.achievements.deserializeUnlocks(data.achievements));
  }

  private tryLoad(subsystem: string, fn: () => unknown) {
    try { fn(); }
    catch (err) {
      console.warn(`[GameStateManager] failed to load '${subsystem}':`, err);
    }
  }

  resetProgress() {
    localStorage.removeItem(SAVE_KEY);
    this.upgrades = new UpgradeSystem();
    this.stats = new StatsSystem();
    this.trader = new TraderSystem();
    this.buildings = new BuildingSystem();
    this.achievements = new AchievementSystem();
    this.money = 0;
    this.labyrinth = null;
    this.exploration = null;
    this.wireAchievementHooks();
  }

  exportSave(): string {
    return btoa(JSON.stringify(this.buildSaveData()));
  }

  importSave(encoded: string): boolean {
    let parsed: unknown;
    try {
      parsed = JSON.parse(atob(encoded));
    } catch {
      return false;
    }

    const data = migrate(parsed);
    if (!data) return false;

    this.labyrinth = null;
    this.exploration = null;
    this.applySave(data);
    this.save();
    return true;
  }

  /** Current map exploration percentage (0-100). */
  getExplorationPct(): number {
    if (!this.exploration || !this.labyrinth) return 0;
    const walkable = this.labyrinth.walkableCount;
    return walkable > 0 ? Math.round((this.exploration.visited.size / walkable) * 100) : 0;
  }

  /** Initialize persistent exploration data (called once after map loads) */
  initExploration(labyrinth: Labyrinth) {
    this.labyrinth = labyrinth;
    if (!this.exploration ||
        this.exploration.mapWidth !== labyrinth.width ||
        this.exploration.mapHeight !== labyrinth.height) {
      this.exploration = new ExplorationData(labyrinth.width, labyrinth.height);
    }
    // Initialize building system base bounds
    this.buildings.initBaseBounds(labyrinth);
    // Always reveal base and door tiles
    for (let y = 0; y < labyrinth.height; y++) {
      for (let x = 0; x < labyrinth.width; x++) {
        const t = labyrinth.tiles[y]![x];
        if (t === Tile.Base || t === Tile.Door) {
          this.exploration.revealTile(x, y);
        }
      }
    }
  }

  switchTo(state: GameState) {
    const prev = this.currentState;
    // Move the pointer first so a throwing exit() cannot leave the manager
    // holding a half-torn-down state. Any exit/save error is logged but does
    // not block the transition to the new state.
    this.currentState = state;
    if (prev) {
      try {
        prev.exit();
        this.save(); // autosave on every state transition
      } catch (err) {
        console.error('[GameStateManager] exit/save failed during switchTo:', err);
      }
    }
    this.currentState.enter();
  }

  update(dt: number) {
    this.achievements.stats.secondsPlayed += dt;
    this.currentState?.update(dt);
  }

  get state(): GameState | null {
    return this.currentState;
  }
}
